import FleetModal, { useAsyncModal } from '@/libs/antd/components/Modal';
import FleetTitle from '@/libs/antd/components/Title';
import { useForm } from '@/libs/antd/form';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import rule from '@/libs/antd/rules';
import { FileData } from '@/libs/fleet-api/services/types';
import { uploader } from '@/libs/fleet-api/uploader';
import { useUserData } from '@/libs/fleet-api/hooks';
import {
	LoadingOutlined,
	UploadOutlined,
	EyeOutlined,
} from '@ant-design/icons';
import { useMutation } from '@tanstack/react-query';
import { Form, Upload, UploadFile, Alert, Progress, Button } from 'antd';
import { FC, useState } from 'react';

export const useSPKDocumentModal = () =>
	useAsyncModal<{
		customerId: string;
		prospectId: string;
		transactionIndex: number;
		currentFile?: FileData;
	}>();

const SPKDocumentModal: FC<ReturnType<typeof useSPKDocumentModal>> = ({
	modal,
	data,
	close,
}) => {
	const { form, fieldName } = useForm<{ document: FileData }>();
	const [uploadProgress, setUploadProgress] = useState(0);
	const [isUploading, setIsUploading] = useState(false);
	const userData = useUserData();

	// Check if user is FRM role
	const isFRM = userData?.groupName === 'FRM';
	const canUpload = isFRM;

	const uploadMutation = useMutation({
		mutationFn: uploader,
	});

	const handleUpload = async (file: File) => {
		if (!file) return;

		setIsUploading(true);
		setUploadProgress(0);

		const fileid = Date.now().toString();
		const filepath = `customer/${data!.customerId}/${data!.prospectId}/spk`;

		try {
			const url = await uploadMutation.mutateAsync({
				file,
				fileid,
				filepath,
				onProgress: (percentage) => {
					setUploadProgress(percentage);
				},
			});

			const fileData: FileData = {
				format: file.type,
				id: fileid,
				name: file.name,
				path: filepath,
				url,
			};

			form.setFieldValue(fieldName('document'), fileData);
			close(fileData);
		} catch (error) {
			console.error('SPK Document upload failed:', error);
			form.setFieldValue(fieldName('document'), null);
		} finally {
			setIsUploading(false);
			setUploadProgress(0);
		}
	};

	const acceptedFileTypes = ['.pdf', '.jpg', '.jpeg', '.png'];
	const maxFileSize = 10 * 1024 * 1024; // 10MB

	const beforeUpload = (file: File) => {
		const isAcceptedType = acceptedFileTypes.some((type) =>
			file.name.toLowerCase().endsWith(type),
		);

		if (!isAcceptedType) {
			console.error('File type not accepted');
			return false;
		}

		if (file.size > maxFileSize) {
			console.error('File size too large');
			return false;
		}

		handleUpload(file);
		return false; // Prevent default upload behavior
	};

	return (
		<FleetModal
			{...modal}
			width={600}
			closeIcon={false}
			keyboard={false}
			maskClosable={!isUploading}
			buttonPosition="end"
			okText="CLOSE"
			cancelText="CANCEL"
			cancelButtonProps={{
				hidden: true,
			}}
			onCancel={() => !isUploading && close()}
			onOk={() => !isUploading && close()}
			afterOpenChange={(isOpen) => {
				if (!isOpen) form.resetFields();
			}}
		>
			<div style={{ marginBottom: 20 }}>
				<FleetTitle>
					{canUpload ? 'Upload Dokumen SPK' : 'View Dokumen SPK'}
				</FleetTitle>
			</div>

			<Alert
				message="Informasi"
				description={
					canUpload ? (
						<div>
							<p>
								Silakan upload dokumen Surat Pemesanan Kendaraan dengan format:
							</p>
							<ul>
								<li>PDF, JPG, JPEG, atau PNG</li>
								<li>Maksimal 10MB</li>
								<li>Pastikan dokumen terlihat jelas dan lengkap</li>
							</ul>
						</div>
					) : (
						<div>
							<p>Anda hanya dapat melihat dokumen SPK.</p>
							<p>
								Hanya role FRM yang dapat melakukan upload atau replace dokumen
								SPK.
							</p>
						</div>
					)
				}
				type={canUpload ? 'info' : 'warning'}
				showIcon
				style={{ marginBottom: 20 }}
			/>

			{data?.currentFile && (
				<Alert
					message="Dokumen SPK sudah ada"
					description={`File: ${data.currentFile.name}`}
					type="warning"
					showIcon
					style={{ marginBottom: 20 }}
					action={
						<Button
							size="small"
							type="link"
						>
							<a
								href={data.currentFile.url}
								target="_blank"
								rel="noopener noreferrer"
							>
								View Document
							</a>
						</Button>
					}
				/>
			)}

			{canUpload ? (
				<Form
					{...ANTD_DEFAULT_PROPS.FORM}
					form={form}
					onFinish={close}
				>
					<Form.Item
						name={fieldName('document')}
						label="Upload Dokumen SPK"
						rules={[rule.required()]}
						labelCol={{ span: 24 }}
						wrapperCol={{ span: 24 }}
						getValueProps={(value) => {
							const file = value as FileData;
							return {
								fileList: file?.id
									? ([
											{
												uid: file.id,
												name: file.name,
												thumbUrl: file.url,
												type: file.format,
											},
										] as UploadFile[])
									: [],
							};
						}}
						normalize={({ file }: { file: UploadFile }) => {
							return {
								id: file.uid,
								format: file.type,
								name: file.name,
								url: file.thumbUrl,
							} as FileData;
						}}
					>
						<Upload.Dragger
							listType="picture"
							maxCount={1}
							beforeUpload={beforeUpload}
							onRemove={() => {
								form.setFieldValue(fieldName('document'), null);
								return false;
							}}
							accept={acceptedFileTypes.join(',')}
							disabled={isUploading}
							showUploadList={{
								showPreviewIcon: true,
								showRemoveIcon: true,
								showDownloadIcon: false,
							}}
						>
							{isUploading ? (
								<div style={{ textAlign: 'center' }}>
									<LoadingOutlined style={{ fontSize: 24, color: '#1890ff' }} />
									<div style={{ marginTop: 8 }}>
										<Progress
											percent={uploadProgress}
											size="small"
										/>
									</div>
									<p style={{ marginTop: 8 }}>Uploading...</p>
								</div>
							) : (
								<div style={{ textAlign: 'center' }}>
									<UploadOutlined style={{ fontSize: 24, color: '#1890ff' }} />
									<p style={{ marginTop: 8 }}>Click or drag file to upload</p>
									<p style={{ fontSize: 12, color: '#999' }}>
										Support: PDF, JPG, JPEG, PNG (Max: 10MB)
									</p>
								</div>
							)}
						</Upload.Dragger>
					</Form.Item>

					{uploadMutation.isError && (
						<Alert
							message="Upload Failed"
							description="There was an error uploading your file. Please try again."
							type="error"
							showIcon
							style={{ marginTop: 16 }}
						/>
					)}
				</Form>
			) : (
				<div style={{ textAlign: 'center', padding: '20px' }}>
					{/* <EyeOutlined style={{ fontSize: 48, color: '#1890ff' }} />
					<p style={{ marginTop: 16, fontSize: 16 }}>View Only Mode</p>
					<p style={{ fontSize: 14, color: '#666' }}>
						Anda tidak memiliki izin untuk upload atau replace dokumen SPK
					</p> */}
					{data?.currentFile && (
						<Button
							type="primary"
							icon={<EyeOutlined />}
							style={{ marginTop: 16 }}
							onClick={() => window.open(data.currentFile!.url, '_blank')}
						>
							View Document
						</Button>
					)}
				</div>
			)}
		</FleetModal>
	);
};

export default SPKDocumentModal;
